/**
 * Spectral Analysis Service
 * Phase 2: Automated Signal Processing
 *
 * Provides FFT-based frequency domain analysis for EEG signals:
 * - Fast Fourier Transform (FFT) computation
 * - Power Spectral Density (PSD) calculation
 * - Band power extraction (Delta, Theta, Alpha, Beta, Gamma)
 * - Dominant frequency identification
 * - Spectrogram generation for time-frequency analysis
 *
 * Clinical Applications:
 * - Seizure analysis (increased Delta/Theta power)
 * - Sleep staging (sleep spindle detection in Sigma band)
 * - Cognitive state monitoring (Alpha rhythm assessment)
 * - Drug effects monitoring (Beta power changes)
 */

export interface FrequencyBand {
  name: string;
  minHz: number;
  maxHz: number;
  color?: string;
}

export interface BandPowerResult {
  name: any;
  power: number;
  band: FrequencyBand;
  absolutePower: number;
  relativePower: number;
  dominantFrequency: number;
}

export interface SpectralResult {
  frequencies: number[];
  powerSpectrum: number[];
  bandPowers: BandPowerResult[];
  dominantFrequency: number;
  dominantPower: number;
  totalPower: number;
  processingTime: number;
}

export interface SpectrogramResult {
  times: number[];
  frequencies: number[];
  spectrogram: number[][];
  bandPowers: { [bandName: string]: number[] };
  processingTime: number;
}

/**
 * Standard EEG Frequency Bands (Clinical)
 */
export const CLINICAL_BANDS: FrequencyBand[] = [
  { name: "Delta", minHz: 0.5, maxHz: 4, color: "#FF6B6B" },
  { name: "Theta", minHz: 4, maxHz: 8, color: "#4ECDC4" },
  { name: "Alpha", minHz: 8, maxHz: 13, color: "#45B7D1" },
  { name: "Beta", minHz: 13, maxHz: 30, color: "#96CEB4" },
  { name: "Gamma", minHz: 30, maxHz: 100, color: "#FFEAA7" },
];

/**
 * Extended Frequency Bands (Research)
 */
export const RESEARCH_BANDS: FrequencyBand[] = [
  ...CLINICAL_BANDS,
  { name: "Slow Delta", minHz: 0.5, maxHz: 2, color: "#D63031" },
  { name: "Fast Delta", minHz: 2, maxHz: 4, color: "#E17055" },
  { name: "Low Beta", minHz: 13, maxHz: 20, color: "#74B9FF" },
  { name: "High Beta", minHz: 20, maxHz: 30, color: "#0984E3" },
  { name: "Ripple", minHz: 80, maxHz: 250, color: "#FDCB6E" },
  { name: "Fast Ripple", minHz: 250, maxHz: 500, color: "#E17055" },
];

class SpectralAnalysisService {
  /**
   * Compute FFT-based power spectrum for a single channel
   *
   * @param signal - Input signal (time domain)
   * @param samplingRate - Sampling rate in Hz
   * @param windowType - Window function ('hann', 'hamming', 'blackman', 'none')
   * @param bands - Frequency bands to analyze
   * @returns Spectral analysis result with band powers
   */
  static computePowerSpectrum(
    signal: number[],
    samplingRate: number,
    windowType: "hann" | "hamming" | "blackman" | "none" = "hann",
    bands: FrequencyBand[] = CLINICAL_BANDS,
  ): SpectralResult {
    const startTime = performance.now();

    // Validate input
    if (!signal || signal.length === 0) {
      throw new Error("Invalid signal data");
    }
    if (samplingRate <= 0) {
      throw new Error("Sampling rate must be positive");
    }

    // Apply window function
    const windowedSignal = this.applyWindow(signal, windowType);

    // Compute FFT
    const fftResult = this.fft(windowedSignal);

    // Calculate power spectrum (magnitude squared)
    const N = signal.length;
    const powerSpectrum = fftResult.map((complex, i) => {
      const magnitude = Math.sqrt(
        complex.real * complex.real + complex.imag * complex.imag,
      );
      // Normalize by N and apply scaling for one-sided spectrum
      return i === 0 || i === N / 2
        ? (magnitude * magnitude) / (N * N)
        : (2 * magnitude * magnitude) / (N * N);
    });

    // Generate frequency bins (only positive frequencies)
    const frequencies = Array.from(
      { length: Math.floor(N / 2) + 1 },
      (_, i) => (i * samplingRate) / N,
    );

    // Trim to Nyquist frequency
    const nyquistIndex = Math.floor(N / 2) + 1;
    const trimmedPowerSpectrum = powerSpectrum.slice(0, nyquistIndex);

    // Calculate band powers
    const totalPower = trimmedPowerSpectrum.reduce((sum, p) => sum + p, 0);
    const bandPowers = this.calculateBandPowers(
      frequencies,
      trimmedPowerSpectrum,
      bands,
      totalPower,
    );

    // Find dominant frequency
    let maxPower = 0;
    let dominantFreqIndex = 0;
    for (let i = 0; i < trimmedPowerSpectrum.length; i++) {
      if (trimmedPowerSpectrum[i] > maxPower) {
        maxPower = trimmedPowerSpectrum[i];
        dominantFreqIndex = i;
      }
    }

    const processingTime = performance.now() - startTime;

    return {
      frequencies,
      powerSpectrum: trimmedPowerSpectrum,
      bandPowers,
      dominantFrequency: frequencies[dominantFreqIndex],
      dominantPower: maxPower,
      totalPower,
      processingTime,
    };
  }

  /**
   * Compute spectrogram (time-frequency representation)
   *
   * @param signal - Input signal
   * @param samplingRate - Sampling rate in Hz
   * @param windowSizeMs - Window size in milliseconds
   * @param overlapPercent - Overlap percentage (0-100)
   * @param bands - Frequency bands to track
   * @returns Spectrogram with time, frequency, and power data
   */
  static computeSpectrogram(
    signal: number[],
    samplingRate: number,
    windowSizeMs: number = 1000,
    overlapPercent: number = 50,
    bands: FrequencyBand[] = CLINICAL_BANDS,
  ): SpectrogramResult {
    const startTime = performance.now();

    const windowSize = Math.floor((windowSizeMs / 1000) * samplingRate);
    const hopSize = Math.floor(windowSize * (1 - overlapPercent / 100));

    const times: number[] = [];
    const spectrogram: number[][] = [];
    const bandPowerTimeSeries: { [bandName: string]: number[] } = {};

    // Initialize band power arrays
    bands.forEach((band) => {
      bandPowerTimeSeries[band.name] = [];
    });

    // Sliding window FFT
    for (let start = 0; start + windowSize <= signal.length; start += hopSize) {
      const window = signal.slice(start, start + windowSize);
      const timeSeconds = start / samplingRate;
      times.push(timeSeconds);

      const result = this.computePowerSpectrum(
        window,
        samplingRate,
        "hann",
        bands,
      );
      spectrogram.push(result.powerSpectrum);

      // Track band powers over time
      result.bandPowers.forEach((bp) => {
        bandPowerTimeSeries[bp.band.name].push(bp.absolutePower);
      });
    }

    const processingTime = performance.now() - startTime;

    // Use frequencies from first window (consistent across all windows)
    const firstWindow = signal.slice(0, windowSize);
    const firstResult = this.computePowerSpectrum(
      firstWindow,
      samplingRate,
      "hann",
      bands,
    );

    return {
      times,
      frequencies: firstResult.frequencies,
      spectrogram,
      bandPowers: bandPowerTimeSeries,
      processingTime,
    };
  }

  /**
   * Apply window function to reduce spectral leakage
   */
  private static applyWindow(signal: number[], windowType: string): number[] {
    const N = signal.length;
    const windowed = new Array(N);

    for (let i = 0; i < N; i++) {
      let w = 1.0;

      switch (windowType) {
        case "hann":
          w = 0.5 * (1 - Math.cos((2 * Math.PI * i) / (N - 1)));
          break;
        case "hamming":
          w = 0.54 - 0.46 * Math.cos((2 * Math.PI * i) / (N - 1));
          break;
        case "blackman":
          w =
            0.42 -
            0.5 * Math.cos((2 * Math.PI * i) / (N - 1)) +
            0.08 * Math.cos((4 * Math.PI * i) / (N - 1));
          break;
        case "none":
          w = 1.0;
          break;
      }

      windowed[i] = signal[i] * w;
    }

    return windowed;
  }

  /**
   * Fast Fourier Transform (Cooley-Tukey radix-2 FFT)
   * Returns complex numbers as {real, imag}
   */
  private static fft(signal: number[]): { real: number; imag: number }[] {
    const N = signal.length;

    // Base case
    if (N === 1) {
      return [{ real: signal[0], imag: 0 }];
    }

    // Pad to nearest power of 2
    const paddedN = Math.pow(2, Math.ceil(Math.log2(N)));
    const padded = [...signal, ...new Array(paddedN - N).fill(0)];

    return this.fftRecursive(padded);
  }

  private static fftRecursive(
    signal: number[],
  ): { real: number; imag: number }[] {
    const N = signal.length;

    if (N === 1) {
      return [{ real: signal[0], imag: 0 }];
    }

    // Divide
    const even = signal.filter((_, i) => i % 2 === 0);
    const odd = signal.filter((_, i) => i % 2 === 1);

    // Conquer
    const evenFFT = this.fftRecursive(even);
    const oddFFT = this.fftRecursive(odd);

    // Combine
    const combined: { real: number; imag: number }[] = new Array(N);

    for (let k = 0; k < N / 2; k++) {
      const angle = (-2 * Math.PI * k) / N;
      const twiddle = { real: Math.cos(angle), imag: Math.sin(angle) };

      // Complex multiplication: oddFFT[k] * twiddle
      const twiddled = {
        real: oddFFT[k].real * twiddle.real - oddFFT[k].imag * twiddle.imag,
        imag: oddFFT[k].real * twiddle.imag + oddFFT[k].imag * twiddle.real,
      };

      combined[k] = {
        real: evenFFT[k].real + twiddled.real,
        imag: evenFFT[k].imag + twiddled.imag,
      };

      combined[k + N / 2] = {
        real: evenFFT[k].real - twiddled.real,
        imag: evenFFT[k].imag - twiddled.imag,
      };
    }

    return combined;
  }

  /**
   * Calculate band powers for specified frequency bands
   */
  private static calculateBandPowers(
    frequencies: number[],
    powerSpectrum: number[],
    bands: FrequencyBand[],
    totalPower: number,
  ): BandPowerResult[] {
    return bands.map((band) => {
      // Find indices for band limits
      const startIndex = frequencies.findIndex((f) => f >= band.minHz);
      const endIndex = frequencies.findIndex((f) => f > band.maxHz);

      const bandIndices =
        startIndex === -1 || endIndex === -1
          ? []
          : powerSpectrum.slice(
              startIndex,
              endIndex === -1 ? undefined : endIndex,
            );

      // Calculate absolute power (sum of power in band)
      const absolutePower = bandIndices.reduce((sum, p) => sum + p, 0);

      // Calculate relative power (percentage of total power)
      const relativePower =
        totalPower > 0 ? (absolutePower / totalPower) * 100 : 0;

      // Find dominant frequency in band
      let maxPower = 0;
      let dominantFreqIndex = startIndex;
      for (
        let i = startIndex;
        i < (endIndex === -1 ? frequencies.length : endIndex);
        i++
      ) {
        if (powerSpectrum[i] > maxPower) {
          maxPower = powerSpectrum[i];
          dominantFreqIndex = i;
        }
      }

      return {
        name: band.name,
        power: absolutePower,
        band,
        absolutePower,
        relativePower,
        dominantFrequency: frequencies[dominantFreqIndex] || 0,
      };
    });
  }

  /**
   * Multi-channel spectral analysis
   */
  static computeMultiChannelSpectrum(
    channels: number[][],
    samplingRate: number,
    bands: FrequencyBand[] = CLINICAL_BANDS,
  ): SpectralResult[] {
    return channels.map((channel) =>
      this.computePowerSpectrum(channel, samplingRate, "hann", bands),
    );
  }
}

export default SpectralAnalysisService;
